import { useMutation, useQueryClient } from '@tanstack/react-query';
import { post } from '@/shared/api/api-client';
import type { CalendarSettings } from '@/types/settings-api';
import { calendarSettingsListQueryKey } from './use-calendar-settings-list';

/**
 * Save the practitioner's per-practice calendar settings (PROD-762).
 *   POST /api/Doctor/CalenderProfile        ← typo "Calender" verbatim
 *
 * **Magic-string traps captured from the working dev cURL 2026-05-20**:
 *
 *   1. URL path is `/api/Doctor/CalenderProfile` — MISSPELLED "Calender"
 *      (without the second "a"). The correctly-spelled "CalendarProfile"
 *      returns 404. Same misspelling on the GET list path
 *      (`/api/Doctor/calenderList`) — see `use-calendar-settings-list.ts`.
 *
 *   2. Body is a **BARE ARRAY** of `CalendarSettings` entries — NOT
 *      wrapped in an envelope like `{ request: [...], isDeleted: [] }`
 *      the way AppointmentProfile and AppointmentTemplate POSTs are.
 *      Each entry is one practice's settings; the array contains one
 *      entry per practice the practitioner is associated with.
 *
 *   3. `providerId` on the body is CORRECTLY spelled — distinct from
 *      the typo'd `practionerId` query string on the GET surface.
 *      Don't auto-correct either direction.
 *
 *   4. `isUpdated: true` flags the entries the user changed in this
 *      submission; `false` for entries that were preserved unchanged.
 *      Server side reads this to decide which rows to write through.
 *      Mirrors Flutter `EnableProviderScheduleEvent` dispatch
 *      pattern — the bloc sets `isUpdated = true` only on the row the
 *      user edited, then sends the FULL list.
 *
 * Flutter parity: `settings_data_source_impl.dart` POST handler for
 * the calendar profile save action. Wire shape verified against
 * captured cURL 2026-05-20.
 *
 * Cache invalidation: only the calendar-settings-list slot. No by-id
 * cache for this surface (unlike Working Hours), so single key.
 */
export function useSaveCalendarSettings(
  doctorSetupId: string,
  organizationId: string,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (entries: CalendarSettings[]) => {
      return post<unknown>('/api/Doctor/CalenderProfile', entries);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: calendarSettingsListQueryKey(doctorSetupId, organizationId),
      });
    },
  });
}
